import React, { useEffect, useState } from "react";
import { Plus, MapPin, Pencil, Trash2, Loader2, Monitor, Radio, X, CalendarClock, CheckCircle2, AlertCircle } from "lucide-react";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500 };

export default function Locations() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", address: "" });
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastRefresh, setBroadcastRefresh] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/locations");
      setLocations(r.data.locations || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", address: "" }); setDialogOpen(true); };
  const openEdit = (l) => { setEditing(l); setForm({ name: l.name, address: l.address || "" }); setDialogOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editing) {
        await api.put(`/locations/${editing.id}`, form);
        toast.success("Location updated");
      } else {
        await api.post("/locations", form);
        toast.success("Location created");
      }
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.name}"? Screens at this location will be unassigned.`)) return;
    try {
      await api.delete(`/locations/${l.id}`);
      toast.success("Location deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-10 max-w-6xl text-slate-900">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-3">Multi-Location</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-semibold" style={headingFont}>
              Run all your <span style={accentFont} className="text-sky-600">stores</span> from one place
            </h1>
            <p className="mt-3 text-sm text-slate-500 max-w-2xl">
              Create one location per store, café, or branch. Assign TVs to a location, then broadcast a design or
              playlist to <strong>every screen</strong> across selected locations in one click.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setBroadcastOpen(true)}
              disabled={locations.length === 0}
              className="rounded-full bg-violet-600 hover:bg-violet-700 text-white shadow-sm"
              data-testid="open-broadcast"
            >
              <Radio className="h-4 w-4 mr-1.5" /> Broadcast
            </Button>
            <Button onClick={openNew} className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm" data-testid="new-location-btn">
              <Plus className="h-4 w-4 mr-1.5" /> New location
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
        ) : locations.length === 0 ? (
          <div className="rounded-3xl border border-dashed p-12 text-center" style={{ background: "#F8FAFC", borderColor: "#E2E8F0" }} data-testid="empty-locations">
            <MapPin className="h-10 w-10 mx-auto text-slate-400 mb-4" />
            <h3 className="text-xl font-semibold mb-2" style={headingFont}>No locations yet</h3>
            <p className="text-sm text-slate-500 max-w-sm mx-auto mb-5">Create your first store, café, or branch to start grouping TVs.</p>
            <Button onClick={openNew} className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm">
              <Plus className="h-4 w-4 mr-1.5" /> Create first location
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="locations-grid">
            {locations.map((l) => (
              <div key={l.id} className="rounded-2xl border bg-white border-slate-200 p-5 hover:shadow-md transition" data-testid={`location-${l.id}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate" style={headingFont}>{l.name}</div>
                    {l.address && <div className="text-xs text-slate-500 truncate">{l.address}</div>}
                  </div>
                  <div className="flex flex-shrink-0 gap-1">
                    <button onClick={() => openEdit(l)} className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500" title="Edit" data-testid={`edit-location-${l.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => remove(l)} className="h-8 w-8 rounded-md hover:bg-rose-50 flex items-center justify-center text-rose-500" title="Delete" data-testid={`delete-location-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <Monitor className="h-3.5 w-3.5 text-sky-500" />
                  <span><strong>{l.screen_count}</strong> screen{l.screen_count === 1 ? "" : "s"}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create / Edit dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="location-dialog">
            <DialogHeader><DialogTitle style={headingFont}>{editing ? "Edit location" : "New location"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Name</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Downtown Café" data-testid="location-name" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Address <span className="opacity-60 normal-case">(optional)</span></label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="e.g. 100 Main St" data-testid="location-address" />
              </div>
              <Button onClick={save} className="w-full rounded-full bg-sky-500 hover:bg-sky-600" data-testid="location-save">{editing ? "Save changes" : "Create location"}</Button>
            </div>
          </DialogContent>
        </Dialog>

        <BroadcastDialog
          open={broadcastOpen}
          onClose={() => setBroadcastOpen(false)}
          locations={locations}
          onDone={() => { load(); setBroadcastRefresh((n) => n + 1); }}
        />

        <BroadcastsList refreshTick={broadcastRefresh} />
      </div>
    </DashboardLayout>
  );
}

function BroadcastDialog({ open, onClose, locations, onDone }) {
  const [picked, setPicked] = useState(new Set());
  const [designs, setDesigns] = useState([]);
  const [designId, setDesignId] = useState("");
  const [mode, setMode] = useState("now");      // "now" | "later"
  const [scheduleAt, setScheduleAt] = useState(""); // YYYY-MM-DDTHH:mm (local)
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setPicked(new Set()); setDesignId(""); setMode("now"); setScheduleAt(""); setName("");
      return;
    }
    (async () => {
      try {
        const r = await api.get("/designs");
        setDesigns(r.data.designs || []);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
    // default schedule = 1 hour from now (local)
    const dt = new Date(Date.now() + 60 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    setScheduleAt(`${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`);
  }, [open]);

  const toggle = (id) => {
    const next = new Set(picked);
    next.has(id) ? next.delete(id) : next.add(id);
    setPicked(next);
  };
  const selectAll = () => setPicked(new Set(locations.map((l) => l.id)));

  const submit = async () => {
    if (picked.size === 0) { toast.error("Pick at least one location"); return; }
    if (!designId) { toast.error("Pick a design"); return; }
    setBusy(true);
    try {
      if (mode === "now") {
        // Existing immediate broadcast endpoint
        const r = await api.post("/locations/broadcast", {
          location_ids: Array.from(picked),
          design_id: designId,
        });
        toast.success(`Broadcast — ${r.data.screens_updated} screen(s) updated across ${r.data.locations_affected} location(s)`);
      } else {
        if (!scheduleAt) { toast.error("Pick a date and time"); setBusy(false); return; }
        // Convert local datetime-local to an ISO string with local timezone
        const localIso = new Date(scheduleAt).toISOString();
        await api.post("/broadcasts", {
          name: name || undefined,
          location_ids: Array.from(picked),
          design_id: designId,
          schedule_at: localIso,
        });
        toast.success("Broadcast scheduled");
      }
      onClose();
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl" data-testid="broadcast-dialog">
        <DialogHeader>
          <DialogTitle style={headingFont}>Broadcast a design</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Mode switch */}
          <div className="flex gap-2 p-1 bg-slate-100 rounded-lg w-fit">
            {[
              { id: "now",   label: "Send now",       icon: Radio },
              { id: "later", label: "Schedule later", icon: CalendarClock },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setMode(id)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-md flex items-center gap-1.5 ${mode === id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
                data-testid={`broadcast-mode-${id}`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>

          {/* Locations */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center justify-between">
              <span>Pick locations</span>
              {locations.length > 0 && (
                <button onClick={selectAll} className="text-[11px] text-sky-600 hover:underline normal-case" data-testid="broadcast-select-all">
                  Select all ({locations.length})
                </button>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-2 max-h-[170px] overflow-y-auto pr-1">
              {locations.map((l) => {
                const on = picked.has(l.id);
                return (
                  <button
                    key={l.id}
                    onClick={() => toggle(l.id)}
                    className={`text-left p-3 rounded-xl border transition ${on ? "border-sky-400 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid={`broadcast-loc-${l.id}`}
                  >
                    <div className="text-sm font-semibold">{l.name}</div>
                    <div className="text-[11px] text-slate-500">{l.screen_count} screen{l.screen_count === 1 ? "" : "s"}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Design */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Pick a design</div>
            <select
              value={designId}
              onChange={(e) => setDesignId(e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm"
              data-testid="broadcast-design-select"
            >
              <option value="">— Choose —</option>
              {designs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>

          {/* Schedule fields (only when later) */}
          {mode === "later" && (
            <div className="grid sm:grid-cols-2 gap-3 p-3 rounded-xl bg-violet-50/60 border border-violet-100">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Send at (your local time)</label>
                <input
                  type="datetime-local"
                  value={scheduleAt}
                  onChange={(e) => setScheduleAt(e.target.value)}
                  className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm bg-white"
                  data-testid="broadcast-schedule-at"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Label <span className="opacity-60 normal-case">(optional)</span></label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Lunch menu drop"
                  className="h-10 text-sm bg-white"
                  data-testid="broadcast-name"
                />
              </div>
            </div>
          )}

          <Button
            onClick={submit}
            disabled={busy || picked.size === 0 || !designId}
            className="w-full h-11 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold"
            data-testid="broadcast-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : mode === "now" ? <Radio className="h-4 w-4 mr-2" />
                                   : <CalendarClock className="h-4 w-4 mr-2" />}
            {mode === "now"
              ? `Broadcast to ${picked.size} location${picked.size === 1 ? "" : "s"} now`
              : `Schedule for ${picked.size} location${picked.size === 1 ? "" : "s"}`}
          </Button>
          <div className="text-[11px] text-slate-400 text-center">
            {mode === "now"
              ? "All screens at the selected locations will switch within a few seconds."
              : "All screens at the selected locations will switch automatically at the scheduled time."}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BroadcastsList({ refreshTick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/broadcasts");
      setItems(r.data.broadcasts || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [refreshTick]);
  useEffect(() => {
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, []);

  const cancel = async (b) => {
    if (!window.confirm("Cancel this scheduled broadcast?")) return;
    try {
      await api.delete(`/broadcasts/${b.id}`);
      toast.success("Broadcast cancelled");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <div className="mt-10" data-testid="broadcasts-list">
      <div className="text-xs uppercase tracking-[0.3em] text-slate-500 mb-3">Recent & upcoming broadcasts</div>
      <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100">
        {items.slice(0, 8).map((b) => {
          const when = new Date(b.schedule_at);
          const isPending = b.status === "pending";
          const isSent    = b.status === "sent";
          const isFailed  = b.status === "failed";
          const isCancelled = b.status === "cancelled";
          const StatusIcon = isPending ? CalendarClock : isSent ? CheckCircle2 : isFailed ? AlertCircle : X;
          const statusColor = isPending ? "text-violet-600 bg-violet-50"
                            : isSent ? "text-emerald-600 bg-emerald-50"
                            : isFailed ? "text-rose-600 bg-rose-50"
                            : "text-slate-500 bg-slate-50";
          return (
            <div key={b.id} className="flex items-center gap-3 p-3 px-4" data-testid={`broadcast-${b.id}`}>
              <div className={`h-9 w-9 rounded-full flex items-center justify-center ${statusColor} flex-shrink-0`}>
                <StatusIcon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate" style={headingFont}>{b.name || (isPending ? "Scheduled broadcast" : "Broadcast")}</div>
                <div className="text-xs text-slate-500 truncate">
                  {when.toLocaleString()} · {b.location_ids?.length || 0} location{(b.location_ids?.length || 0) === 1 ? "" : "s"}
                  {isSent && ` · ${b.screens_updated || 0} screen(s)`}
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">
                {b.status}
              </div>
              {isPending && (
                <button onClick={() => cancel(b)} className="h-8 w-8 rounded-md hover:bg-rose-50 flex items-center justify-center text-rose-500" data-testid={`broadcast-cancel-${b.id}`} title="Cancel">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
